import {Slot, useNavigationContainerRef} from "expo-router";
import {AuthProvider} from "@/auth/AuthProvider";
import {UploadProvider} from "@/contexts/UploadContext";
import {UploadProgressToast} from "@/components/media/media-library/UploadProgressToast";
import {ToastProvider} from "@/contexts/ToastContext";
import {Toast} from "@/components/common/Toast";
import {useTranslation} from "react-i18next";
import {makeZodI18nMap} from "zod-i18n-map";
import {z} from "zod";
import {loadSavedLanguage} from '@/lib/i18n';
import {AppVersion} from "@/components/common/AppVersion";
import {QueryClient} from "@tanstack/query-core";
import {appConfig} from "@/app-config";
import {PersistQueryClientProvider} from "@tanstack/react-query-persist-client";
import {persistOptions, registerOnlineManager} from "@/data/app-data-persister";
import {Suspense, useEffect} from "react";
import {AppLoadingScreen} from "@/components/common/AppLoadingScreen";
import {ErrorBoundaryWrapper} from "@/components/common/ErrorBoundaryWrapper";
import {BaseThemeProvider} from "@/theming/BaseThemeProvider";
import {StatusBarMatcher} from "@/components/common/StatusBarMatcher";
import AlertProvider from "@/lib/AlertProvider";
import {WebMaxWidthContainer} from "@/components/common/WebMaxWidthContainer";
import {NotificationTapHandler} from "@/components/common/NotificationTapHandler";
import {configureForegroundNotifications} from "@/lib/notification-foreground";
import * as Sentry from "@sentry/react-native";

const navigationIntegration = Sentry.reactNavigationIntegration({
    enableTimeToInitialDisplay: true,
});

Sentry.init({
    dsn: appConfig.sentryDsn,
    release: appConfig.sentryRelease,
    tracesSampleRate: 0.2,
    enableAutoSessionTracking: true,
    integrations: [navigationIntegration],
    enabled: !!appConfig.sentryDsn,
});

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            gcTime: appConfig.appDataPersistence.gcTime
        },
    },
});

registerOnlineManager();

// At module scope, next to the other one-time wiring: the OS can hand a notification to a
// foreground app before any component of ours has mounted, and a handler installed later would
// miss it.
configureForegroundNotifications();

function RootLayout() {
    const ref = useNavigationContainerRef();
    const {t} = useTranslation();
    z.setErrorMap(makeZodI18nMap({t}));

    useEffect(() => {
        loadSavedLanguage();
    }, []);

    if (ref?.current) {
        navigationIntegration.registerNavigationContainer(ref);
    }

    return (
        <BaseThemeProvider>
            <AlertProvider>
                <WebMaxWidthContainer>
                    <PersistQueryClientProvider
                        client={queryClient}
                        persistOptions={persistOptions}
                    >
                        <AuthProvider>
                            {/* Above the auth gate on purpose — a notification tapped while
                                logged out has to be heard too. Inside <AuthProvider> because it
                                needs to know whether it may navigate straight away. */}
                            <NotificationTapHandler/>
                            <UploadProvider>
                                <ToastProvider>
                                    <ErrorBoundaryWrapper>
                                        <Suspense fallback={<AppLoadingScreen/>}>
                                            <Slot/>
                                        </Suspense>
                                    </ErrorBoundaryWrapper>
                                    <UploadProgressToast/>
                                    <Toast/>
                                    <AppVersion/>
                                </ToastProvider>
                            </UploadProvider>
                        </AuthProvider>
                    </PersistQueryClientProvider>
                    <StatusBarMatcher/>
                </WebMaxWidthContainer>
            </AlertProvider>
        </BaseThemeProvider>
    );
}

export default Sentry.wrap(RootLayout);
